import React, { useState, useRef, useEffect } from 'react';
import {
  Bars3Icon,
  BellIcon,
  CheckIcon,
  BoltIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import SearchBar from './SearchBar';
import UserMenuDropdown from './topbar/UserMenuDropdown';
import TopBarThemeToggle from './topbar/TopBarThemeToggle';
import './TopBar.css';

function TopBar({
  searchQuery,
  onSearch = () => {},
  searchResults = null,
  onResultClick = () => {},
  notifications = [],
  showNotifications = false,
  setShowNotifications = () => {},
  onMarkNotificationRead = () => {},
  onClearAllNotifications = () => {},
  userName = '',
  userEmail = '',
  onNavigate = () => {},
  onLogout = () => {},
}) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchResultsRef = useRef(null);
  const notificationsRef = useRef(null);
  const userMenuRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (searchResultsRef.current && !searchResultsRef.current.contains(e.target)) {
        // Parent handles clearing search results
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="mp-topbar">
      <div className="mp-topbar__inner">
        <button
          type="button"
          className="mp-topbar__icon-btn mp-topbar__menu-btn"
          aria-label="Abrir menu lateral"
          onClick={() => window.dispatchEvent(new CustomEvent('mp:sidebar-toggle'))}
        >
          <Bars3Icon className="mp-topbar__icon-svg" />
        </button>

        {/* ── Search ── */}
        <div className="mp-topbar__search" ref={searchResultsRef}>
          <SearchBar
            placeholder="Buscar templates, posts, campanhas..."
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
          />

          {/* Search Results Dropdown */}
          {searchResults && searchResults.total > 0 && (
            <div className="mp-topbar__dropdown mp-topbar__dropdown--search">
              <div className="mp-topbar__dropdown-header">
                {searchResults.total} resultado(s) encontrado(s)
              </div>

              {searchResults.templates?.length > 0 && (
                <div className="mp-topbar__dropdown-group">
                  <h4 className="mp-topbar__dropdown-label">Templates ({searchResults.templates.length})</h4>
                  {searchResults.templates.map((template, index) => (
                    <div
                      key={template.id || `template-${index}`}
                      className="mp-topbar__dropdown-item"
                      onClick={() => onResultClick('creator')}
                    >
                      <h5>{template.name || template.title || 'Template sem nome'}</h5>
                      <p>
                        {template.category && <span className="mp-topbar__tag">#{template.category}</span>}
                        {template.category && template.prompt && ' • '}
                        {template.prompt && template.prompt.substring(0, 80)}
                        {template.prompt?.length > 80 && '...'}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {searchResults.posts?.length > 0 && (
                <div className="mp-topbar__dropdown-group">
                  <h4 className="mp-topbar__dropdown-label">Posts Gerados ({searchResults.posts.length})</h4>
                  {searchResults.posts.map((post, index) => (
                    <div
                      key={post.id || `post-${index}`}
                      className="mp-topbar__dropdown-item"
                      onClick={() => onResultClick('contents')}
                    >
                      <h5>{post.content?.substring(0, 50) || 'Post sem conteúdo'}...</h5>
                      <p>
                        {post.platform && <span className="mp-topbar__tag mp-topbar__tag--blue">{post.platform}</span>}
                        {post.created_at && ` • ${new Date(post.created_at).toLocaleDateString('pt-BR')}`}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {searchResults && searchResults.total === 0 && (
            <div className="mp-topbar__dropdown mp-topbar__dropdown--search">
              <div className="mp-topbar__dropdown-empty">
                Nenhum resultado encontrado para "{searchQuery}"
              </div>
            </div>
          )}
        </div>

        {/* ── Actions ── */}
        <div className="mp-topbar__actions">
        <TopBarThemeToggle />
        {/* Notifications */}
        <div className="mp-topbar__action-wrapper" ref={notificationsRef}>
          <button
            className="mp-topbar__icon-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <BellIcon className="mp-topbar__icon-svg" />
            {unreadCount > 0 && (
              <span className="mp-topbar__badge">{unreadCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="mp-topbar__dropdown mp-topbar__dropdown--notifications">
              <div className="mp-topbar__dropdown-header">
                <span>Notificações</span>
                {notifications.length > 0 && (
                  <button className="mp-topbar__link-btn" onClick={onClearAllNotifications}>
                    Limpar tudo
                  </button>
                )}
              </div>

              <div className="mp-topbar__dropdown-body">
                {notifications.length === 0 ? (
                  <div className="mp-topbar__dropdown-empty">
                    <BellIcon className="mp-topbar__empty-icon" />
                    <p>Nenhuma notificação</p>
                  </div>
                ) : (
                  notifications.map(notif => (
                    <div
                      key={notif.id}
                      className={`mp-topbar__notif ${!notif.read ? 'is-unread' : ''}`}
                      onClick={() => onMarkNotificationRead(notif.id)}
                    >
                      <div className={`mp-topbar__notif-icon mp-topbar__notif-icon--${notif.type}`}>
                        {notif.type === 'success' && <CheckIcon className="mp-topbar__notif-icon-svg" />}
                        {notif.type === 'energy' && <BoltIcon className="mp-topbar__notif-icon-svg" />}
                        {notif.type === 'info' && <BellIcon className="mp-topbar__notif-icon-svg" />}
                      </div>
                      <div className="mp-topbar__notif-body">
                        <p>{notif.message}</p>
                        <span>{notif.time}</span>
                      </div>
                      {!notif.read && <div className="mp-topbar__notif-dot" />}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="mp-topbar__action-wrapper" ref={userMenuRef}>
          <button
            className="mp-topbar__user-btn"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="mp-avatar mp-avatar--sm">
              {userName.charAt(0).toUpperCase()}
            </div>
            <span className="mp-topbar__user-name">{userName}</span>
            <ChevronDownIcon className={`mp-topbar__chevron ${userMenuOpen ? 'is-open' : ''}`} />
          </button>

          {userMenuOpen && (
            <div className="mp-topbar__dropdown mp-topbar__dropdown--user">
              <UserMenuDropdown
                userName={userName}
                userEmail={userEmail}
                onNavigate={onNavigate}
                onLogout={onLogout}
                onClose={() => setUserMenuOpen(false)}
              />
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

export default TopBar;
